import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Bike, Gavel, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useClienteAuthStore } from '@/stores/clienteAuthStore';
import { clienteMobileService } from '@/services/clienteMobileService';
import type { ClienteTitulo } from '@/types/clienteMobile';
import { formatCurrency } from '@/lib/formatters';
import { formatDate } from '@/lib/utils';
import { openClienteWhatsApp } from '@/utils/clienteWhatsApp';
import { getFinanceiroWhatsAppE164 } from '@/utils/financeiroWhatsApp';

type FilterType = 'vencidos' | 'a_vencer' | 'pagos';

function getTipoBoleto(t: ClienteTitulo): 'LOCACAO' | 'MULTA' {
  const o = (t.observacoes ?? '').toLowerCase();
  if (o.includes('multa') || o.includes('infração') || o.includes('infracao') || o.includes('auto')) return 'MULTA';
  return 'LOCACAO';
}

export function ClienteFinanceiroPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useClienteAuthStore((s) => s.user);

  const rawFilter = searchParams.get('filter') as FilterType | null;
  const filter: FilterType =
    rawFilter === 'vencidos' || rawFilter === 'a_vencer' || rawFilter === 'pagos' ? rawFilter : 'vencidos';
  const showMultas = searchParams.get('multas') === '1' || searchParams.get('multas') === 'true';

  const [titulos, setTitulos] = useState<ClienteTitulo[]>([]);
  const [loading, setLoading] = useState(true);

  const setFilter = (f: FilterType) => {
    const next = new URLSearchParams(searchParams);
    next.set('filter', f);
    if (showMultas) next.set('multas', '1');
    else next.delete('multas');
    setSearchParams(next);
  };

  const setShowMultas = (m: boolean) => {
    const next = new URLSearchParams(searchParams);
    next.set('filter', filter);
    if (m) next.set('multas', '1');
    else next.delete('multas');
    setSearchParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tipo = filter === 'vencidos' ? 'vencido' : filter === 'pagos' ? 'pago' : 'a_vencer';
      const list = await clienteMobileService.getTitulos({ tipo });
      setTitulos(list);
    } catch {
      toast.error('Erro ao carregar títulos.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const locacoes = useMemo(() => titulos.filter((t) => getTipoBoleto(t) === 'LOCACAO'), [titulos]);
  const multas = useMemo(() => titulos.filter((t) => getTipoBoleto(t) === 'MULTA'), [titulos]);
  const displayed = showMultas ? multas : locacoes;

  const segundaVia = (t: ClienteTitulo) => {
    if (t.urlBoleto?.trim()) {
      let url = t.urlBoleto.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) url = `https://${url}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    const phone = getFinanceiroWhatsAppE164(user?.clienteFornecedor?.company?.name);
    openClienteWhatsApp(phone, `Desejo regularizar meus débitos do título ${t.numero}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex rounded-xl border border-border p-1 bg-muted/40 gap-1">
        <button
          type="button"
          onClick={() => setShowMultas(false)}
          className={`flex-1 flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition-colors ${
            !showMultas ? 'bg-button-brand text-button-brand-foreground' : 'text-muted-foreground hover:bg-background'
          }`}
        >
          <Bike className="h-4 w-4" />
          Locação
          {locacoes.length > 0 ? (
            <span className="text-xs rounded-full bg-black/10 px-1.5">{locacoes.length}</span>
          ) : null}
        </button>
        <button
          type="button"
          onClick={() => setShowMultas(true)}
          className={`flex-1 flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition-colors ${
            showMultas ? 'bg-button-brand text-button-brand-foreground' : 'text-muted-foreground hover:bg-background'
          }`}
        >
          <Gavel className="h-4 w-4" />
          Multas
          {multas.length > 0 ? (
            <span className="text-xs rounded-full bg-black/10 px-1.5">{multas.length}</span>
          ) : null}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {(['vencidos', 'a_vencer', 'pagos'] as const).map((f) => (
          <Button
            key={f}
            type="button"
            size="sm"
            variant={filter === f ? 'default' : 'outline'}
            className={filter === f ? 'bg-zinc-900 text-white' : ''}
            onClick={() => setFilter(f)}
          >
            {f === 'vencidos' ? 'Vencidos' : f === 'a_vencer' ? 'A vencer' : 'Pagos'}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : displayed.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">Nenhum item nesta lista.</p>
      ) : (
        <ul className="space-y-3">
          {displayed.map((t) => (
            <li key={t.id}>
              <Card>
                <CardContent className="pt-4 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold">Título {t.numero}</p>
                    <p className="text-sm text-muted-foreground">Venc. {formatDate(t.vencimento)}</p>
                    <p className="text-lg font-bold mt-1">{formatCurrency(t.valor)}</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => segundaVia(t)}>
                    2ª via / regularizar
                  </Button>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
